import { Grid, Switch } from '@mui/material';
import { Location } from '@nx-smartmonkey/shared/domain';
import { RoutalPalette, SMPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useIntl } from 'react-intl';

import InputLocation from '../../../../react-components/InputLocation';
import InputText from '../../../../react-components/InputText';
import Text from '../../../../react-components/Text';

export interface ProjectData {
  label?: string;
  description?: string;
  location?: Location;
  traffic?: boolean;
}

type NewProjectFormProps = {
  newProject: ProjectData;
  changeProject: (project: ProjectData) => void;
};

export const NewProjectForm = ({ newProject, changeProject }: NewProjectFormProps) => {
  const intl = useIntl();
  return (
    <Grid container direction="column" style={{ maxWidth: `740px`, padding: `0 20px` }}>
      <Grid item>
        <Text variant="h6" intlId="projects.create.new" />
      </Grid>

      <VSpacer />

      <Grid item container direction="column">
        <InputText
          id="label"
          type="text"
          label={<Text tagName="span" intlId="projects.create.label" />}
          fieldValue={newProject.label}
          onChange={(value: any) => changeProject({ ...newProject, label: value })}
          required
          validators={[
            {
              errorMessage: (
                <Text
                  tagName="span"
                  style={{ color: RoutalPalette.error.medium }}
                  intlId="settings.error.field_mandatory"
                />
              ),
              isInvalid: (text?: string) => (text ?? ``).length === 0,
            },
          ]}
        />
      </Grid>

      <VSpacer medium />

      <Grid item container direction="column">
        <Text tagName="span" style={{ marginBottom: `4px` }}>
          <Text tagName="span" intlId="projects.create.location" />
          <Text tagName="span" variant="body" style={{ cursor: `default`, color: SMPalette.danger }}>
            *
          </Text>
        </Text>

        <VSpacer small />

        <InputLocation
          fieldValue={newProject.location ?? undefined}
          inputProps={{
            styles: {},
            size: `medium`,
            variant: `outlined`,
            placeholder: intl.formatMessage({ id: `location` }),
          }}
          searchWithoutProjectId
          emptyIntlId="no_results"
          fullWidth
          onUpdate={(newLocation?: Location | null) => {
            changeProject({ ...newProject, location: newLocation ?? undefined });
          }}
        />
      </Grid>

      <VSpacer medium />

      <Grid item container direction="column">
        <InputText
          fieldValue={newProject.description}
          label={<Text tagName="span" intlId="projects.create.description" />}
          multiline
          inputProps={{
            maxLength: 512,
          }}
          rowsMin={2}
          rowsMax={4}
          aria-label="Project description"
          placeholder={intl.formatMessage({
            id: `projects.create.description_ph`,
          })}
          onChange={(value) => changeProject({ ...newProject, description: value })}
        />
      </Grid>

      <VSpacer medium />

      <Grid item container direction="row" wrap="nowrap">
        <Grid item container direction="column" wrap="nowrap">
          <Grid item>
            <Text tagName="span" intlId="projects.create.traffic" />
          </Grid>

          <VSpacer small />

          <Grid item>
            <Text variant="label" intlId="projects.create.traffic.description" />
          </Grid>
        </Grid>

        <Grid item>
          <Switch
            checked={newProject.traffic}
            value={newProject.traffic}
            disableRipple
            onClick={(event) => event.stopPropagation()}
            onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
              changeProject({ ...newProject, traffic: checked });
            }}
            color="primary"
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
